<script setup>
import { formatDateString } from '@/composables/helpers/dates';
import { useWindowStore } from '@/stores/window';
import { storeToRefs } from 'pinia';

const props = defineProps({
    cardImage: {
        type: String,
        required: true,
    },
    altText: {
        type: String,
    },
    headline: {
        type: String,
        required: true,
        default: 'Headline',
    },
    excerpt: {
        type: String,
        default: 'Excerpt',
    },
    category: {
        type: String,
        required: true,
    },
    date: {
        type: [Number, String, Date],
        required: true,
    },
    tags: {
        type: Array,
        required: true,
        default: () => [],
    },
    tagColor: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    link: {
        type: String,
        default: '',
    },
    replace: {
        type: Boolean,
        default: false,
    }
});

const { innerWidth } = storeToRefs(useWindowStore());
const postsContainer = ref(null);

function hideHiddenPosts() {
    if (postsContainer.value) {
        const postsArray = postsContainer.value.children;
        const arr = [...postsArray];
        arr.forEach((tag) => {
            tag.offsetTop > 0 ? tag.setAttribute('aria-hidden', 'true') : tag.setAttribute('aria-hidden', 'false');
        });
    }
}

function handleResize() {
    hideHiddenPosts();
}

watch(innerWidth, () => {
    handleResize();
});

const emit = defineEmits(['clicked']);

const handleClick = (type, value) => {
    emit('clicked', { type, value });
};

</script>

<template>
    <section class="blog-card" :class="[size, size == 'small' ? 'flex-container-small' : 'flex-container']"
        tabindex="-1">
        <template v-if="!loading">
            <div class="image-container">
                <MessLink :href="link" v-if="link" :label="headline">
                    <MessImage :src="cardImage" :alt="altText ? altText : 'The featured image for this article.'" />
                </MessLink>
                <MessImage v-else :src="cardImage" :alt="altText ? altText : 'The featured image for this article.'" />
            </div>
            <div class="inner-flex">
                <div class="details-container">
                    <ProjectTag :link="`/blog?category=${category.toLowerCase()}`" category :useHover="true" colorSet="orange"
                        :text="category" :replace="replace" @click="handleClick('category', category)"
                        gtmEventName="blog_category_tag_clicked" />
                    <MessHtml tag="span" class="caps date fs-12" :html="formatDateString(date)" />
                </div>
                <template v-if="link" >
                    <MessLink :href="link" :label="headline">
                    <MessHtml tag="h3" class="headline" :class="[
                            { 'fs-24 large': size == 'large' },
                            { 'fs-24 medium': size == 'medium' },
                            { 'fs-20 small': size == 'small' },
                        ]" :html="headline" />
                    </MessLink>
                    <MessHtml v-if="size != 'small' && excerpt" class="excerpt" :html="excerpt" :class="{ 'fs-17 large': size == 'large' }" />
                </template>
                <div v-else>
                    <MessHtml tag="h3" class="headline" :class="[
                        { 'fs-24 large': size == 'large' },
                        { 'fs-24 medium': size == 'medium' },
                        { 'fs-20 small': size == 'small' },
                    ]" :html="headline" />
                    <MessHtml v-if="size != 'small' && excerpt" class="excerpt" :html="excerpt" />
                </div>
                <div class="tag-container" v-if="tags.length" ref="postsContainer">
                    <ProjectTag v-for="(tag, index) in tags" :key="index" :replace="replace" :useHover="true"
                        :colorSet="tagColor || 'transparent'"
                        :text="tag"
                        link="tags"
                        text-only
                        tabindex="0"
                        @keydown.enter.prevent="handleClick('tag', tag)"
                        @click.prevent="handleClick('tag', tag)"
                        gtmEventName="blog_tag_clicked" />
                </div>
            </div>
        </template>
        <template v-else>
            <FragmentsCardsSkeleton v-for="i in 12" :key="i" />
        </template>
    </section>
</template>

<style lang="scss">
.blog-card {
    
    .image-container {
        border-radius: var(--radius);
        overflow: hidden;
    }

    .mess-image {
        height: auto;
        width: 100%;
        aspect-ratio: 1.6283524904;
        transition: 0.33s ease-in-out;
        border-radius: var(--radius);
        overflow: hidden;

        @media(hover:hover) {
            &:hover {
                transform: scale(var(--scale));
            }
        }
    }

    a {
        transition: 250ms ease;
    }

    .headline {
        text-transform: none;
        font-family: 'Lexend Deca';
        font-weight: 649;
        color: var(--teal);

        &:hover {
            color: var(--orange-dark);
        }

        &.large {
            font-weight: 500;
            line-height: 3rem;
            letter-spacing: -0.045rem;
        }

        &.medium {
            font-weight: 600;
            line-height: 2rem;
            letter-spacing: -0.045rem;
        }

        &.small {
            font-weight: 600;
            line-height: 1.8125rem;
            letter-spacing: -0.039375rem;
        }
    }

    &.flex-container {
        display: flex;
        flex-direction: column;

        .inner-flex {
            flex-grow: 1;
            display: flex;
            flex-direction: column;

            .tag-container {
                position: relative;
            }

            .excerpt {
                overflow: hidden;
                color: var(--teal);
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
            }
        }
    }

    &.flex-container-small {
        display: flex;
        margin-bottom: 2rem;

        .image-container {
            width: 37%;
            aspect-ratio: 3/2;
            align-self: flex-start;
            margin-bottom: unset;
        }

        .inner-flex {
            display: flex;
            flex-direction: column;
            margin-left: 5%;
            // width: 58%;
            width: calc(100% - 37% - 5%);

            .details-container {
                margin-bottom: 1rem;
            }

            div:last-child .headline {
                margin-bottom: 2.9rem;
            }

            .headline {
                margin-bottom: 1.5rem;
            }
        }

        .tag-container {
            margin-top: 0;
            position: relative;
        }
    }

    .image-container {
        overflow: hidden;
    }

    .image-container,
    .excerpt {
        margin-bottom: var(--box-padding-tb-m);
        color: var(--teal);
        font-size: 0.9375rem;
        line-height: 1.5625rem;
    }

    .details-container {
        margin-bottom: var(--box-padding-tb-m);
        display: flex;
        align-items: center;

        .date {
            margin-left: 1.5rem;
            font-weight: 400;
            color: var(--teal);
            letter-spacing: 0.0375rem;
        }
    }

    .headline {
        margin-bottom: 1.4rem;
        line-height: 2rem;
        color: var(--teal);
        font-weight: 600;
        letter-spacing: -0.04375rem;
    }

    .tag-container {
        margin-top: auto;
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        max-height: 2.0625rem;
        overflow: clip;

        .project-tag {
            margin-right: 0;
            letter-spacing: 0.034375rem;
            color: var(--teal-gray);
            border-color: var(--teal-gray);
        }
    }

    .tag-container {
        margin-top: auto;
        display: flex;
        flex-wrap: wrap;
        row-gap: 0.8rem;
    }

    @media (max-width: $mobile) {
        .date.fs-14 {
            font-size: 12px;
        }

        &.flex-container-small {
            margin-bottom: unset;

            .image-container {
                margin-bottom: 1.5rem;
            }

            .tag-container {
                margin-bottom: 2.9rem;
            }

            .inner-flex .details-container {
                margin-bottom: 1.4rem;
            }
        }
    }

    @media (max-width: 680px) {
        &.flex-container-small {
            flex-direction: column;

            .image-container {
                width: 100%;
            }

            .inner-flex {
                margin-left: 0;
                width: 100%;
            }
        }
    }

    &.featured-post {
        
        h3 {
            font-size: calc(36 / var(--base-fs) * 1rem);
            font-weight: 500;
            line-height: 2.5rem;
            color: var(--teal);
            letter-spacing: -0.045rem;
        }

        .excerpt {
            font-size: calc(17 / var(--base-fs) * 1rem);
            font-weight: 300;   
            color: var(--teal);
            line-height: 1.75rem;
            letter-spacing: 0.02125rem;
        }

    }
}
</style>
